import { z } from 'zod';
import { dayBefore, narrowWindow, type DateWindow } from '@/lib/format/format';
import type { EventsQuery } from './api';
import { EVENT_TABS, type EventTab } from './vocab';

export const EVENTS_PAGE_SIZE = 25;

/** The catalog's view, kept in the URL. */
export const eventsSearchSchema = z.object({
  status: z.enum(EVENT_TABS).default('live').catch('live'),
  q: z.string().trim().min(1).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
});

export type EventsSearch = z.output<typeof eventsSearchSchema>;

const FILTERS = ['q', 'from', 'to'] as const satisfies (keyof EventsSearch)[];

export const noFilters: Partial<EventsSearch> = { q: undefined, from: undefined, to: undefined };

export const hasFilters = (search: EventsSearch): boolean =>
  FILTERS.some((key) => search[key] !== undefined);

/** A tab's dates: live from today, past until yesterday (Belgrade). */
export const tabWindow = (tab: EventTab, today: string): DateWindow =>
  tab === 'live' ? { start: today } : { end: dayBefore(today) };

/** The tab's dates narrowed by the date filter, or null when nothing can match. */
export function dateWindow(
  tab: EventTab,
  from: string | undefined,
  to: string | undefined,
  today: string,
): DateWindow | null {
  return narrowWindow(tabWindow(tab, today), from, to);
}

/** The catalog read for this view. */
export const toEventsQuery = (search: EventsSearch, today: string): EventsQuery => ({
  keyword: search.q,
  window: dateWindow(search.status, search.from, search.to, today),
  latestFirst: search.status === 'past',
  page: search.page,
  size: EVENTS_PAGE_SIZE,
});
